import { useEffect, useMemo, useRef, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { ResourcesResponse } from "@rago/web-shared";
import { getSource, useSources } from "../../../sources";
import { ServerSource } from "../../../sources/serverSource";
import { problemMessage } from "../../../lib/authManager";
import { getPrefs, patchPrefs, type ServerPrefs } from "../../../lib/libraryAdmin";
import {
  fieldOfProblem,
  formFromPrefs,
  generalPatch,
  librarySummary,
  NAME_MAX,
  sameForm,
  updateStatus,
  validateForm,
  type GeneralForm,
} from "../../../lib/generalAdmin";
import {
  formatBuildDate,
  formatUptime,
  getServerAbout,
  platformLabel,
  shortCommit,
  uptimeSecondsAt,
} from "../../../lib/aboutAdmin";
import { useServers, type SettingsServer } from "../../../lib/servers";
import { useAdvancedSettings } from "../../../lib/settingsAdvanced";
import { since } from "../../../lib/timeAgo";
import {
  Checkbox,
  Chip,
  ErrorNote,
  errorMessage,
  Field,
  InfoRow,
  Spinner,
  StatusDot,
} from "../../../components/ui";
import {
  AdvancedToggle,
  LeadRow,
  saveOnEnter,
  SheetHeader,
  SheetLoading,
  SheetSection,
  UnsavedBar,
  useDraft,
  useLastTouched,
} from "../../../components/settings-sheet";
import { ServerSettingsGuard } from "../../../components/server-settings";
import { RemoveServerButton } from "../../../components/server-remove";

export const Route = createFileRoute("/_authed/settings/server/$serverId/general")({
  component: ServerGeneral,
});

/**
 * A rename is server-side truth the moment the PATCH succeeds, but the
 * chip, sidebar, and titles all render the broker's copy — write it into
 * the cache immediately, then reconcile with broker truth after the kicked
 * heartbeat has landed (one HTTP round-trip; 4 s is comfortable).
 */
function propagateRename(qc: QueryClient, serverId: string, name: string) {
  qc.setQueryData<ResourcesResponse>(["resources"], (old) =>
    old
      ? { ...old, items: old.items.map((it) => (it.serverId === serverId ? { ...it, name } : it)) }
      : old,
  );
  void qc.invalidateQueries({ queryKey: ["nav", "servers"] });
  window.setTimeout(() => {
    void qc.invalidateQueries({ queryKey: ["resources"] });
  }, 4000);
}

/**
 * "Check for updates" re-reads the broker's view (which stamps the newest
 * published build onto /v1/resources) and the server's own about facts,
 * so both the version chip and a post-upgrade build/uptime repaint.
 */
function CheckUpdatesButton({ serverId }: { serverId: string }) {
  const qc = useQueryClient();
  const [phase, setPhase] = useState<"idle" | "checking" | "checked">("idle");
  const timer = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    [],
  );

  async function check() {
    setPhase("checking");
    await Promise.allSettled([
      qc.refetchQueries({ queryKey: ["resources"] }),
      qc.invalidateQueries({ queryKey: ["settings", "about", serverId] }),
    ]);
    setPhase("checked");
    timer.current = window.setTimeout(() => setPhase("idle"), 5000);
  }

  return (
    <button
      type="button"
      className="rago-btn rago-btn-quiet rago-btn-sm"
      disabled={phase !== "idle"}
      onClick={() => void check()}
    >
      {phase === "checking" ? <Spinner size={12} /> : null}
      {phase === "checking"
        ? "Checking…"
        : phase === "checked"
          ? "Checked just now"
          : "Check for updates"}
    </button>
  );
}

/**
 * The facts grid: the broker's version + update notice and presence on the
 * left, the server's own build stamp and platform on the right. The about
 * route is owner-only and newer than some servers — either way the grid
 * degrades to the broker rows without an error box.
 */
function AboutSection({ server, source }: { server: SettingsServer; source: ServerSource | null }) {
  const about = useQuery({
    queryKey: ["settings", "about", server.id],
    enabled: Boolean(source) && server.owned && server.online,
    staleTime: 30_000,
    queryFn: () => getServerAbout(source!.conn),
  });
  // Uptime ticks once a minute from startedAt — exact at any age, so no
  // refetch is needed for it.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const info = about.data ?? null;
  const update = updateStatus(server.version, server.latestVersion);
  const uptime = info ? formatUptime(uptimeSecondsAt(info, about.dataUpdatedAt, now)) : null;
  const commit = shortCommit(info?.commit);
  const built = formatBuildDate(info?.buildDate);
  const platform = platformLabel(info?.os, info?.arch, info?.docker);

  return (
    <SheetSection
      title="About"
      action={server.owned ? <CheckUpdatesButton serverId={server.id} /> : null}
    >
      <div className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
        <div className="flex flex-col gap-4">
          <InfoRow label="Version">
            <span className="inline-flex flex-wrap items-center gap-2">
              <span className="font-mono text-[13px]">
                {server.version ?? info?.version ?? "Unknown"}
              </span>
              {update === "upToDate" ? (
                <Chip tone="ok">Up to date</Chip>
              ) : update === "updateAvailable" ? (
                <Chip tone="accent">Update available — {server.latestVersion}</Chip>
              ) : null}
            </span>
          </InfoRow>
          <InfoRow label="Status">
            <StatusDot online={server.online} />
            {!server.online
              ? "Offline"
              : server.brokerOnline
                ? "Online"
                : `Online · reached directly${server.lastSeenAt ? `, last check-in ${since(server.lastSeenAt)}` : ""}`}
          </InfoRow>
          {uptime ? <InfoRow label="Uptime">{uptime}</InfoRow> : null}
        </div>
        <div className="flex flex-col gap-4">
          {commit ? (
            <InfoRow label="Build">
              <span className="inline-flex flex-wrap items-center gap-2">
                <span className="font-mono text-[13px]">{commit}</span>
                {built ? <span className="text-[var(--rago-text-3)]">{built}</span> : null}
              </span>
            </InfoRow>
          ) : null}
          {platform ? <InfoRow label="Platform">{platform}</InfoRow> : null}
          {!server.owned && server.ownerUsername ? (
            <InfoRow label="Owner">{server.ownerUsername}</InfoRow>
          ) : null}
        </div>
      </div>
    </SheetSection>
  );
}

/** Count + names, sharing the Libraries page's query so both stay in step. */
function LibrariesSection({
  server,
  source,
}: {
  server: SettingsServer;
  source: ServerSource | null;
}) {
  const libraries = useQuery({
    queryKey: ["settings", "libraries", server.id],
    staleTime: 30_000,
    enabled: Boolean(source) && server.online,
    queryFn: () => source!.getLibraries(),
  });
  const summary = libraries.data ? librarySummary(libraries.data) : null;
  // The Libraries page is owner-only; a shared user sees the count alone.
  const manage = server.owned ? (
    <Link
      to="/settings/server/$serverId/libraries"
      params={{ serverId: server.id }}
      className="rago-btn rago-btn-quiet rago-btn-sm select-none"
    >
      Manage libraries →
    </Link>
  ) : null;

  return (
    <SheetSection title="Libraries">
      {libraries.isLoading ? (
        <p className="flex items-center gap-2 text-sm text-[var(--rago-text-2)]">
          <Spinner /> Loading…
        </p>
      ) : summary ? (
        <LeadRow title={summary.headline} hint={summary.line} action={manage} />
      ) : (
        <LeadRow
          title="Libraries unavailable"
          hint="Couldn't load the library list from the server."
          action={manage}
        />
      )}
    </SheetSection>
  );
}

/** Remove, with the claimed-by line as its explanation. Owner only. */
function DangerZone({ server }: { server: SettingsServer }) {
  if (!server.owned) return null;
  const who = server.ownerUsername ? `${server.ownerUsername} (you)` : "you";
  return (
    <SheetSection title="Danger zone" tone="accent">
      <LeadRow
        title="Remove this server"
        hint={`Claimed by ${who}. Removing unclaims it from your account; libraries stay on disk.`}
        action={<RemoveServerButton server={server} />}
      />
    </SheetSection>
  );
}

function ServerGeneral() {
  const { serverId } = Route.useParams();
  // Subscribe to the registry: getSource() is a sync snapshot, and the
  // prefs query would stay disabled forever without the hydration rerender.
  useSources();
  const source = getSource(serverId);
  const src = source instanceof ServerSource ? source : null;
  const conn = src?.conn ?? null;
  const qc = useQueryClient();
  const [advanced] = useAdvancedSettings();
  // Same ["resources"] cache as the guard — for the owner gate before render.
  const { servers } = useServers();
  const owned = servers.find((s) => s.id === serverId)?.owned ?? false;

  const prefs = useQuery({
    queryKey: ["settings", "prefs", serverId],
    enabled: conn !== null && owned,
    queryFn: () => getPrefs(conn!),
  });

  // One draft form seeded from the server's values, following them while it
  // is still clean (settings-sheet useDraft).
  const baseline = useMemo(() => (prefs.data ? formFromPrefs(prefs.data) : null), [prefs.data]);
  const [draft, setDraft] = useDraft(baseline, sameForm);

  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof GeneralForm, string>>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<unknown>(null);
  // The control changed last: it gets the focus back when the pill leaves.
  const touched = useLastTouched();

  const form = draft ?? baseline;
  // Not "the patch has keys": an emptied name is left out of the patch, and
  // still has to raise the pill so that Save can say what is wrong with it.
  const dirty = Boolean(baseline && form && !sameForm(baseline, form));
  const has = (k: keyof ServerPrefs) => Boolean(prefs.data && k in prefs.data);

  function update(p: Partial<GeneralForm>) {
    setDraft((f) => ({ ...(f as GeneralForm), ...p }));
    setSaved(false);
    setError(null);
    setFieldErrors((e) => {
      const next = { ...e };
      for (const k of Object.keys(p) as (keyof GeneralForm)[]) delete next[k];
      return next;
    });
  }

  function revert() {
    setDraft(baseline);
    setError(null);
    setFieldErrors({});
  }

  // The name is the one field with a line of its own: a wrong one takes the
  // focus, which also brings it into view under the floating pill.
  function showFieldError(field: keyof GeneralForm, message: string) {
    setFieldErrors((prev) => ({ ...prev, [field]: message }));
    if (field === "name") document.getElementById("general-name")?.focus();
  }

  // One PATCH for everything that changed. The prefs cache is seeded from
  // the response (PATCH /v1/prefs returns the whole document) and the draft
  // snaps to what the server stored — the name comes back trimmed — unless
  // something was typed while the save was on its way.
  async function save() {
    if (!conn || !form || !baseline || !prefs.data || saving || !dirty) return;
    const errs = validateForm(form);
    if (errs.name) {
      showFieldError("name", errs.name);
      return;
    }
    const patch = generalPatch(baseline, form, prefs.data);
    if (Object.keys(patch).length === 0) {
      // Nothing to send (a key this server lacks): the page is clean.
      setDraft(baseline);
      return;
    }
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const next = await patchPrefs(conn, patch);
      qc.setQueryData(["settings", "prefs", serverId], next);
      setDraft((d) => (d && sameForm(d, form) ? formFromPrefs(next) : d));
      if ("friendlyName" in patch && next.friendlyName) {
        propagateRename(qc, serverId, next.friendlyName);
      }
      setSaved(true);
    } catch (e) {
      const errField = fieldOfProblem(e);
      const message = problemMessage(e);
      if (errField && message) showFieldError(errField, message);
      else setError(e);
    } finally {
      setSaving(false);
    }
  }

  return (
    <ServerSettingsGuard serverId={serverId} heading="General">
      {(server) => (
        // .rago-sheet-fill: the root fills the sheet, so the pill docks at its foot.
        <div className="rago-sheet-fill">
          <SheetHeader
            title="General"
            action={server.owned && has("debugLogging") ? <AdvancedToggle /> : null}
          />

          {!server.owned ? null : prefs.isError ? (
            <SheetSection title="Server">
              <ErrorNote error={prefs.error} />
            </SheetSection>
          ) : prefs.isLoading || !form ? (
            <SheetLoading />
          ) : (
            // Every control's change bubbles to here: the one changed last.
            <div onChange={touched.onChange}>
              <SheetSection title="Server">
                <Field
                  label="Friendly name"
                  htmlFor="general-name"
                  hint="The name everyone sees for this server — in the server switcher, the sidebar, and shared libraries."
                  error={fieldErrors.name}
                >
                  <input
                    id="general-name"
                    className="rago-input"
                    value={form.name}
                    maxLength={NAME_MAX + 10}
                    spellCheck={false}
                    onChange={(e) => update({ name: e.target.value })}
                    onKeyDown={saveOnEnter(() => void save())}
                  />
                </Field>

                {advanced && has("debugLogging") ? (
                  <div className="mb-4">
                    <Checkbox
                      id="general-debug"
                      checked={form.debugLogging}
                      onChange={(v) => update({ debugLogging: v })}
                      label="Enable debug logging"
                      hint="Writes detailed logs to the server's log files (data/logs/rago-*.log) and the Console — takes effect when saved, no restart. On by default; turn off only to keep the files smaller."
                    />
                  </div>
                ) : null}

                {advanced && has("verboseLogging") ? (
                  <Checkbox
                    id="general-verbose"
                    checked={form.verboseLogging}
                    onChange={(v) => update({ verboseLogging: v })}
                    label="Enable verbose logging"
                    hint="Adds request headers, ffmpeg output and metadata-provider round-trips to the log files (not the Console) — takes effect when saved, no restart. Very noisy: turn it on while reproducing a problem, then download the logs from Troubleshooting."
                  />
                ) : null}
              </SheetSection>
            </div>
          )}

          <AboutSection server={server} source={src} />
          <LibrariesSection server={server} source={src} />
          <DangerZone server={server} />

          {/* The root's direct child (its dock rides at the sheet's foot), and
              always rendered, as UnsavedBar asks. Someone the server is shared
              with has no Server section, and so never anything unsaved. */}
          <UnsavedBar
            dirty={dirty}
            saving={saving}
            saved={saved}
            error={error ? errorMessage(error) : null}
            onSave={() => void save()}
            onRevert={revert}
            onFocusLost={touched.focus}
          />
        </div>
      )}
    </ServerSettingsGuard>
  );
}
